import { State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridItemChangeEvent } from '@progress/kendo-react-grid';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  CombineLineItemColumns,
  ResultingLineItemColumns,
} from 'src/assets/static/receipt_details/CombineLineItemColumns';
import GridWrapper from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';

const initialReceiptDataState: State = {
  skip: 0,
  take: 2,
  skipCalculatingTotalRecordsCount: false,
  sort: [
    {
      field: 'poQty',
      dir: 'asc',
    },
  ],
  filters: [],
} as State;

interface CombineLineItemProps {
  combinableLineItems: ReceiptLineItem[];
  close: () => void;
  setReceiptLineItems: React.Dispatch<ReceiptLineItem[]>;
  receiptLineItems: ReceiptLineItem[];
  isPOQtyOfItemWithLeastItemNumZero: boolean;
  itemWithLeastItemNumber: ReceiptLineItem;
}

const CombineLineItems: FC<CombineLineItemProps> = ({
  combinableLineItems,
  close,
  setReceiptLineItems,
  receiptLineItems,
  isPOQtyOfItemWithLeastItemNumZero,
  itemWithLeastItemNumber,
}) => {
  const { setSelectedLineItemIds } = useReceiptDetails();
  const [data, setData] = useState([
    {
      ...itemWithLeastItemNumber,
      currentDocQty: combinableLineItems[0]?.currentDocQty + combinableLineItems[1]?.currentDocQty,
      inEdit: true,
      isLeastItemNumZero: isPOQtyOfItemWithLeastItemNumZero,
      selected: false,
    },
  ]);

  const finalResultingColumns = useMemo(
    () =>
      isPOQtyOfItemWithLeastItemNumZero
        ? ResultingLineItemColumns
        : ResultingLineItemColumns.map((column: any) => {
            const nonEditableColumns = ['item', 'itemDescription', 'poglCode'];
            if (nonEditableColumns.includes(column.field)) {
              column = { ...column, editable: false };
            }
            return column;
          }),
    [isPOQtyOfItemWithLeastItemNumZero]
  );
  const isAnyRequiredFieldEmpty = useMemo(() => {
    if (!data?.[0]) return true;
    const { itemDescription, poglCode, uom } = data?.[0];
    return !!(isEmpty(itemDescription) || isEmpty(poglCode) || isEmpty(uom));
  }, [data]);

  const itemChange = useCallback((e: GridItemChangeEvent) => {
    setData((prev) => {
      const items = [...prev]?.map((item: any) => {
        if (item.ProductID === e.dataItem.ProductID) {
          item[e.field || ''] = e.value;
        }
        return item;
      });
      return items;
    });
  }, []);

  const onApplyClick = useCallback(() => {
    const combinableItemRowIds = combinableLineItems.map((item: ReceiptLineItem) => item.rowId);
    const dataCurrentDocQty: any = data[0].currentDocQty;
    const remainingLineItems: ReceiptLineItem[] = receiptLineItems.filter(
      (lineItem) => !combinableItemRowIds.includes(lineItem.rowId)
    );
    const newLineItem = {
      ...data[0],
      inEdit: true,
      itemNoteCode: 'FULLY_ACCEPTED',
      currentDocQty: parseInt(dataCurrentDocQty),
      itemNoteValue: 'Fully Accepted',
      varianceQty: data[0].poQty - (parseInt(dataCurrentDocQty) + data[0].previousDocQty),
    };
    newLineItem.varianceQty =
      data[0].poQty - (parseInt(dataCurrentDocQty) + data[0].previousDocQty);
    newLineItem.isCombinedLineItem = true;
    const newLineItems = [newLineItem, ...remainingLineItems];
    setReceiptLineItems(newLineItems);
    setSelectedLineItemIds([]);
    close();
  }, [
    close,
    combinableLineItems,
    data,
    receiptLineItems,
    setReceiptLineItems,
    setSelectedLineItemIds,
  ]);

  return (
    <>
      <div className="mt-3">
        Fill the final values for the two items you wish to combine and click "Apply" or click
        "Cancel" to close the dialog
      </div>
      <div>Resulting Receipt Item</div>
      <GridWrapper
        resizable
        columns={finalResultingColumns}
        dataSource={data}
        onItemChange={itemChange}
        dataState={initialReceiptDataState}
        onDataStateChange={() => {}}
        sortable
        total={1}
        DataItemKey="rowId"
        pageable={false}
        tableTextId="lineItems"
      />
      <div className="mt-6">Receipt Items to Combine</div>
      <GridWrapper
        resizable
        columns={CombineLineItemColumns}
        dataSource={combinableLineItems}
        dataState={initialReceiptDataState}
        onDataStateChange={() => {}}
        sortable
        height="150px"
        total={2}
        DataItemKey="rowId"
        pageable={false}
        tableTextId="lineItems"
      />
      <DialogActionsBar>
        <div className="flex justify-end gap-2">
          <Button disabled={isAnyRequiredFieldEmpty} onClick={onApplyClick}>
            Apply
          </Button>
          <Button color="info" onClick={close}>
            Cancel
          </Button>
        </div>
      </DialogActionsBar>
    </>
  );
};

export default CombineLineItems;
